import React, { useMemo } from "react";
import { BarStack, Circle, Line, LinePath } from "@visx/shape";

import { Group } from "@visx/group";
import { GridColumns, GridRows } from "@visx/grid";
import { AxisBottom, AxisLeft } from "@visx/axis";

import { scaleBand, scaleLinear, scaleOrdinal } from "@visx/scale";
import { useTooltip, useTooltipInPortal, defaultStyles } from "@visx/tooltip";
import { localPoint } from "@visx/event";
import { graphql, useFragment } from "react-relay";
import {
  TimeSeriesChart$key,
  TimeSeriesChart$data,
} from "./__generated__/TimeSeriesChart.graphql";
import {
  AHText,
  AHTextFonts,
  Box,
  useLayoutContext,
  useTheme,
  Circle as C,
} from "arkh";
import { curveMonotoneX } from "d3-shape";
import { useFormatValueUnit } from "./formatDuration";
import { AnalyticsValueUnit } from "./__generated__/TimeSeriesOverviewItem_data.graphql";

let tooltipTimeout: number;

type EdgeType = TimeSeriesChart$data["edges"][number];
type TooltipData = {
  bar: EdgeType;
  value: number;
  label: string;
  key: string;
  index: number;
  height: number;
  width: number;
  x: number;
  y: number;
  color: string;
};

export function TimeSeriesChart(props: {
  width: number;
  height: number;
  margin?: { top: number; right: number; bottom: number; left: number };
  data: TimeSeriesChart$key;
  type?: "bars" | "lines";
  selectedDimensions: string[];
  hoverDimension: string | null;
  unit?: AnalyticsValueUnit;
  maxDimensions?: number;
}) {
  const chartType = props.type ?? "lines";
  const data = useFragment(
    graphql`
      fragment TimeSeriesChart on TimeSeriesAnalyticsConnection {
        dimensions {
          id
          label
          color
        }
        edges {
          node {
            date
            formattedDate
            values {
              dimensionId
              value
            }
          }
        }
      }
    `,
    props.data
  );

  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip,
  } = useTooltip<TooltipData>();

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    // TooltipInPortal is rendered in a separate child of <body /> and positioned
    // with page coordinates which should be updated on scroll. consider using
    // Tooltip or TooltipWithBounds if you don't need to render inside a Portal
    scroll: true,
  });
  const theme = useTheme();
  const { width, height } = props;

  const margin = props.margin ?? {
    top: 5,
    right: 0,
    bottom: 0,
    left: 0,
  };

  const timeSeries = useMemo(() => data.edges.slice().reverse(), [data.edges]);

  const maxDimensions = props.maxDimensions ?? 300;
  const dimensions = useMemo(
    () =>
      data.dimensions
        .filter((d) => props.selectedDimensions.includes(d.id))
        .slice(-maxDimensions),
    [data.dimensions, props.selectedDimensions, maxDimensions]
  );

  const keys = useMemo(() => dimensions.map((d) => d.id), [dimensions]);

  const edges = useMemo(
    () =>
      data.edges.map(({ node }) => ({
        node,
        filteredValues: node.values.filter((v) => keys.includes(v.dimensionId)),
      })),
    [data.edges, keys]
  );

  const maxDomainValues =
    chartType === "bars"
      ? edges.map((d) =>
          d.filteredValues?.reduce((acc, curr) => acc + (curr.value ?? 0), 0)
        )
      : edges.map((d) =>
          Math.max(...d.filteredValues.map((v) => v.value ?? 0))
        );
  // accessors
  const getDate = (d: (typeof edges)[number]) => d.node.date;

  // scales
  const dateScale = scaleBand<string>(
    chartType === "bars"
      ? {
          domain: timeSeries.map((d) => d.node.date),
          padding: 0.4,
          paddingOuter: 0.1,
        }
      : {
          domain: timeSeries.map((d) => d.node.date),
          paddingOuter: 0,
          align: 0.5,
          paddingInner: 2,
        }
  );
  const domainScale = scaleLinear<number>({
    round: true,
    domain: [0, Math.max(...maxDomainValues)],
    nice: true,
  });

  const colorScale = scaleOrdinal({
    domain: data.dimensions.map((d) => d.id),
    range: data.dimensions.map((d) => d.color ?? theme.primary),
    unknown: theme.primary,
  });
  const formatValueUnit = useFormatValueUnit();
  if (width < 10) return null;
  // bounds

  const bottomInset = 40;
  const domainInset = 50;
  const xMax = width - domainInset;
  const domainWidth = width - domainInset * 2;
  const yMax = height - margin.top - bottomInset;

  dateScale.rangeRound([domainInset, xMax]);
  domainScale.range([yMax, 0]);

  function handleMouseMove(event: React.MouseEvent<SVGElement>) {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);
    // TooltipInPortal expects coordinates to be relative to containerRef
    // localPoint returns coordinates relative to the nearest SVG, which
    // is what containerRef is set to in this example.
    const eventSvgCoords = localPoint(event);
    if (!eventSvgCoords) return;

    // Find the nearest date based on x position
    const mouseX = eventSvgCoords.x;
    const datePoints = edges.map((edge) => ({
      date: edge.node.date,
      x: dateScale(edge.node.date) ?? 0,
      edge,
    }));

    // Sort by distance to mouse x position to find closest date
    const sortedByX = [...datePoints].sort(
      (a, b) => Math.abs(a.x - mouseX) - Math.abs(b.x - mouseX)
    );
    const nearestDatePoint = sortedByX[0];

    if (chartType === "lines" && nearestDatePoint) {
      const mouseY = eventSvgCoords.y - margin.top;
      const nearestEdge = nearestDatePoint.edge;

      // Find which dimension line is closest to the mouse Y position
      const dimensionPoints = dimensions.map((dimension) => {
        const timePoint = nearestEdge.filteredValues.find(
          (val) => val.dimensionId === dimension.id
        );
        const value = timePoint?.value ?? 0;
        const yPosition = domainScale(value);

        return {
          dimension,
          value,
          y: yPosition,
          distance: Math.abs(yPosition - mouseY),
        };
      });

      // Sort by vertical distance to find closest line
      const sortedByY = [...dimensionPoints].sort(
        (a, b) => a.distance - b.distance
      );
      const nearestDimension = sortedByY[0];

      if (nearestDimension) {
        const left = nearestDatePoint.x;

        // Create a valid TooltipData object that matches the expected structure
        const tooltipData: TooltipData = {
          bar: nearestEdge,
          label: nearestDimension.dimension.label,
          key: nearestDimension.dimension.id,
          index: datePoints.indexOf(nearestDatePoint),
          height: 0,
          width: 0,
          x: nearestDatePoint.x,
          y: nearestDimension.y,
          color: colorScale(nearestDimension.dimension.id),
          value: nearestDimension.value,
        };

        showTooltip({
          tooltipData,
          tooltipTop: nearestDimension.y + margin.top,
          tooltipLeft: left,
        });
      }
    }
  }

  return width < 10 ? null : (
    <Box style={{ position: "relative", width, height }} color="surface">
      <svg
        ref={containerRef}
        width={width}
        height={height}
        onMouseMove={chartType === "lines" ? handleMouseMove : undefined}
        onMouseLeave={
          chartType === "lines"
            ? () => {
                tooltipTimeout = window.setTimeout(() => {
                  hideTooltip();
                }, 300);
              }
            : undefined
        }
      >
        <GridRows
          top={margin.top}
          left={margin.left + domainInset}
          scale={domainScale}
          width={domainWidth}
          height={yMax}
          stroke={theme["border-primary"]}
          strokeOpacity={1}
        />
        {chartType === "lines" ? (
          <>
            <GridColumns
              top={margin.top}
              left={margin.left}
              scale={dateScale}
              width={domainWidth}
              height={yMax}
              stroke={theme["border-primary"]}
              strokeOpacity={1}
            />
            {tooltipOpen && tooltipData && (
              <g>
                <Line
                  from={{ x: tooltipLeft, y: margin.top }}
                  to={{
                    x: tooltipLeft,
                    y: height - bottomInset,
                  }}
                  stroke={theme.darkerText}
                  opacity={0.1}
                  strokeWidth={2}
                  pointerEvents="none"
                  strokeDasharray="5,2"
                />
              </g>
            )}
          </>
        ) : null}

        <Group top={margin.top}>
          <Group>
            {chartType === "lines"
              ? dimensions.map((dimension) => {
                  const isShaded = props.hoverDimension
                    ? props.hoverDimension !== dimension.id
                    : false;
                  return (
                    <LinePath
                      key={dimension.id}
                      curve={curveMonotoneX}
                      data={edges}
                      opacity={isShaded ? 0.1 : 1}
                      stroke={colorScale(dimension.id)}
                      strokeWidth={2}
                      x={(d) => dateScale(d.node.date) ?? 0}
                      y={(d) => {
                        const timePoint = d.filteredValues.find(
                          (val) => val.dimensionId === dimension.id
                        );

                        return domainScale(timePoint?.value ?? 0);
                      }}
                    />
                  );
                })
              : null}
          </Group>
          {chartType === "bars" ? (
            <BarStack
              data={edges}
              keys={keys}
              x={getDate}
              xScale={dateScale}
              yScale={domainScale}
              color={colorScale}
              value={(d, key) => {
                return (
                  d.filteredValues.find((d) => d.dimensionId === key)?.value ??
                  0
                );
              }}
            >
              {(barStacks) =>
                barStacks.map((barStack) => (
                  <Group
                    key={`bar-stack-${barStack.index}`}
                    style={{
                      overflow: "hidden",
                    }}
                  >
                    {barStack.bars.map((bar, i) => {
                      return (
                        <Bar
                          isShaded={
                            props.hoverDimension
                              ? props.hoverDimension !== bar.key
                              : false
                          }
                          key={`bar-${barStack.index}-${bar.index}`}
                          color={colorScale(bar.key)}
                          x={bar.x}
                          y={bar.y}
                          width={bar.width}
                          height={bar.height}
                          onMouseMove={(event) => {
                            if (tooltipTimeout) clearTimeout(tooltipTimeout);
                            // TooltipInPortal expects coordinates to be relative to containerRef
                            // localPoint returns coordinates relative to the nearest SVG, which
                            // is what containerRef is set to in this example.
                            const eventSvgCoords = localPoint(event);
                            const left = bar.x + bar.width / 2;
                            showTooltip({
                              tooltipData: {
                                bar: bar.bar.data,
                                value:
                                  bar.bar.data.filteredValues.find(
                                    (v) => v.dimensionId === bar.key
                                  )?.value ?? 0,
                                label:
                                  dimensions.find((d) => d.id === bar.key)
                                    ?.label ?? bar.key,
                                key: bar.key,
                                index: barStack.index,
                                height: bar.height,
                                width: bar.width,
                                x: bar.x,
                                y: bar.y,
                                color: colorScale(bar.key),
                              },
                              tooltipTop: eventSvgCoords?.y,
                              tooltipLeft: left,
                            });
                          }}
                          onMouseLeave={() => {
                            tooltipTimeout = window.setTimeout(() => {
                              hideTooltip();
                            }, 300);
                          }}
                        />
                      );
                    })}
                  </Group>
                ))
              }
            </BarStack>
          ) : null}
        </Group>
        <AxisLeft
          top={margin.top}
          left={domainInset}
          scale={domainScale}
          strokeWidth={0}
          tickValues={domainScale.ticks()}
          tickStroke={theme.darkerText}
          tickLabelProps={{
            fill: theme.darkText,
            fontSize: 9,
            textAnchor: "end",
            fontFamily: AHTextFonts.BOLD,
          }}
          tickFormat={(value) => {
            return formatValueUnit(value.valueOf(), props.unit ?? "unit");
          }}
        />
        <AxisBottom
          top={yMax + margin.top}
          scale={dateScale}
          tickFormat={(d, i) => {
            return timeSeries[i].node.formattedDate;
          }}
          strokeWidth={0}
          tickLabelProps={{
            fill: theme.darkText,
            fontSize: 9,
            textAnchor: "middle",
            fontFamily: AHTextFonts.BOLD,
          }}
        />
        {tooltipOpen && tooltipData && chartType === "lines" ? (
          <g>
            <Circle
              cx={tooltipLeft}
              cy={tooltipTop}
              r={4}
              fill={tooltipData.color}
            />
          </g>
        ) : null}
      </svg>

      {tooltipOpen && tooltipData && (
        <TooltipInPortal
          top={tooltipTop}
          left={tooltipLeft}
          style={{
            ...defaultStyles,
            boxShadow: "none",
            backgroundColor: "transparent",
            padding: 0,
            margin: 0,
          }}
        >
          <TooltipContent
            tooltipData={tooltipData}
            unit={props.unit ?? "unit"}
          />
        </TooltipInPortal>
      )}
    </Box>
  );
}

function TooltipContent(props: {
  tooltipData: TooltipData;
  unit: AnalyticsValueUnit;
}) {
  const theme = useTheme();
  const formatValueUnit = useFormatValueUnit();

  const { tooltipData } = props;
  if (!tooltipData?.bar?.node) {
    return null;
  }
  return (
    <Box
      space="small"
      spaceHorizontal="medium"
      borderColor="border-strong"
      style={{ borderWidth: 1 }}
      cornerRadius="medium"
      color="surface"
    >
      <AHText
        spaceBottom="xxsmall"
        style={{ color: theme.darkerText }}
        type="detail"
      >
        {tooltipData.bar.node.formattedDate}
      </AHText>
      <Box
        style={{ display: "flex", alignItems: "center", flexDirection: "row" }}
      >
        <C
          size="dot"
          style={{
            backgroundColor: tooltipData.color,
          }}
        />
        <AHText spaceLeft="small" type="subtitle">
          {tooltipData.label}:
        </AHText>

        <AHText spaceLeft="small" type="subtitle">
          {formatValueUnit(tooltipData.value, props.unit)}
        </AHText>
      </Box>
    </Box>
  );
}

function Bar(props: {
  color: string;
  x: number;
  y: number;
  width: number;
  height: number;
  onMouseLeave: () => void;
  onMouseMove: (event: React.MouseEvent<SVGPathElement>) => void;
  isShaded: boolean;
}) {
  const { color, x, y, width, height, onMouseLeave, onMouseMove, isShaded } =
    props;
  const layout = useLayoutContext();
  const radius = layout.radius.small;
  const bar = { x, y, width, height, color };

  return (
    <rect
      rx={radius}
      x={bar.x}
      y={bar.y}
      width={bar.width}
      height={Math.max(bar.height - 1, 0)}
      fill={bar.color}
      opacity={isShaded ? 0.1 : 1}
      style={{
        transition: "opacity 0.1s ease-in-out",
      }}
      onMouseLeave={onMouseLeave}
      onMouseMove={onMouseMove}
    />
  );
}
